import {
  AddCardOutlined,
  AddOutlined,
  PageDS,
  Spacer,
  TabMenu,
  ToastContainerDS,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Cards as CardsTab } from "./cards/cards";
import { Badges as BadgesTab } from "./badges/badges";
import { selectCompany, selectManager } from "../homeSlice";
import { useAppSelector } from "../../../redux/hooks";
import { AddCardModal } from "./modals/cards/addCardModal";
import { AddBadgeModal } from "./modals/badges/addBadgeModal";
import { MandateUnsignedModal } from "./modals/badges/mandateUnsignedModal";
import { selectIsStripePlatformMandateSigned } from "../billings/sepaDebitSlice";
import { selectManagersList } from "../users/usersSlice";
import { supportPhonesByCompanyGroupUuid } from "../../../constants/support";

export const CardsAndBadges = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  const company = useAppSelector(selectCompany);
  const manager = useAppSelector(selectManager);

  const [isCardModalVisible, setIsCardModalVisible] = useState(false);
  const [isMultipleCardsModalVisible, setIsMultipleCardsModalVisible] =
    useState(false);
  const [isCreateBadgeModalVisible, setIsCreateBadgeModalVisible] =
    useState(false);
  const [isModalUnsignedModalVisible, setIsModalUnsignedModalVisible] =
    useState(false);
  const [isMultipleBadgesModalVisible, setIsMultipleBadgesModalVisible] =
    useState(false);

  const tabs = useMemo(() => {
    return [
      {
        name: "cards",
        title: t("cards.tab.cards"),
      },
      {
        name: "badges",
        title: t("cards.tab.badges"),
      },
    ];
  }, [t]);

  const managers = useAppSelector(selectManagersList);

  const otherSupportPhones = useMemo(
    () =>
      managers && managers.length > 0 && managers[0].companyGroup
        ? supportPhonesByCompanyGroupUuid[managers[0].companyGroup]
        : undefined,
    [managers],
  );

  const [selectedTab, setSelectedTab] = useState<string>(params.tab ?? "cards");
  const isStripePlatformMandateSigned = useAppSelector(
    selectIsStripePlatformMandateSigned,
  );

  const addCardIsDisabled = useMemo(
    () =>
      company === null ||
      company.iban === "" ||
      company.iban === null ||
      company.bic === "" ||
      company.bic === null,
    [company],
  );

  const isUserManager = useMemo(
    () => manager && manager.role === "USER",
    [manager],
  );

  const hasMandatRequirements = useMemo(() => {
    if (!company) return false;

    const hasQontoMandate =
      company.providerOnboardingStatus === "FINISHED" &&
      company.externalIban &&
      company.qontoMandateSigned;

    const hasMemoBankMandate =
      company.providerOnboardingStatus === "FINISHED" &&
      company.externalIban &&
      company.memoBankMandateSigned;

    const hasStripePlatformMandate =
      company.externalIban &&
      company.canActivatePlatformSepaDebit &&
      (company.platformSepaMandateSigned || isStripePlatformMandateSigned);

    return hasQontoMandate || hasStripePlatformMandate || hasMemoBankMandate;
  }, [company, isStripePlatformMandateSigned]);

  const cta = useMemo(
    () =>
      selectedTab === "cards"
        ? {
            title: t("cards.cta"),
            action: () => {
              if (!addCardIsDisabled) setIsCardModalVisible(true);
            },
            width: 12,
            isDisabled: addCardIsDisabled,
            leftIcon: <AddOutlined />,
          }
        : {
            title: t("badges.cta"),
            action: () => {
              hasMandatRequirements
                ? setIsCreateBadgeModalVisible(true)
                : setIsModalUnsignedModalVisible(true);
            },
            width: 12,
            leftIcon: <AddOutlined />,
          },
    [addCardIsDisabled, hasMandatRequirements, selectedTab, t],
  );

  const cta2 = useMemo(
    () =>
      selectedTab === "cards"
        ? {
            title: t("cards.cta2"),
            action: () => {
              if (!addCardIsDisabled) setIsMultipleCardsModalVisible(true);
            },
            width: 12,
            isDisabled: addCardIsDisabled,
            leftIcon: <AddCardOutlined />,
          }
        : {
            title: t("badges.cta2"),
            action: () => {
              hasMandatRequirements
                ? setIsMultipleBadgesModalVisible(true)
                : setIsModalUnsignedModalVisible(true);
            },
            width: 12,
            leftIcon: <AddOutlined />,
          },
    [addCardIsDisabled, hasMandatRequirements, selectedTab, t],
  );

  useEffect(() => {
    if (selectedTab) {
      navigate(
        `/home/cards/${selectedTab}${
          params.id !== undefined ? `/${params.id}` : ""
        }`,
      );
    }
  }, [navigate, params.id, selectedTab]);

  if (!company) {
    return;
  }

  if (isMultipleCardsModalVisible) navigate("/home/cards_order_multiple_cards");

  if (isMultipleBadgesModalVisible)
    navigate("/home/cards_order_multiple_badges");

  return (
    <>
      <AddCardModal
        visible={isCardModalVisible}
        onCloseModal={() => {
          setIsCardModalVisible(false);
        }}
      />
      <AddBadgeModal
        visible={isCreateBadgeModalVisible}
        onCloseModal={() => {
          setIsCreateBadgeModalVisible(false);
        }}
      />
      <MandateUnsignedModal
        company={company}
        visible={isModalUnsignedModalVisible}
        onCloseModal={() => {
          setIsModalUnsignedModalVisible(false);
        }}
      />
      <PageDS
        title={t(`cards.tab.${selectedTab}`)}
        cta={isUserManager ? undefined : cta}
        cta2={isUserManager ? undefined : cta2}
        toaster={<ToastContainerDS />}
        topElement={
          <>
            <TabMenu
              tabs={tabs}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
            />
            <Spacer y={0.5} />
          </>
        }
        supportButton={{
          showMail: !otherSupportPhones,
          otherPhones: otherSupportPhones,
        }}
      >
        <CardsTab />
        <BadgesTab />
      </PageDS>
    </>
  );
};
